'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string | null;
  role?: {
    id: string;
    name: string;
    description?: string | null;
  };
  permissions?: Array<{
    id: string;
    resource: string;
    action: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; user?: User; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
const ACTIVITY_SYNC_KEY = 'auth:last-activity-at';
const ACTIVITY_HEARTBEAT_MS = 60 * 1000; // persist at most once per minute

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPersistedActivityRef = useRef(0);

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const persistActivityTimestamp = (timestamp: number) => {
    if (timestamp - lastPersistedActivityRef.current < ACTIVITY_HEARTBEAT_MS) {
      return;
    }

    lastPersistedActivityRef.current = timestamp;
    try {
      localStorage.setItem(ACTIVITY_SYNC_KEY, String(timestamp));
    } catch {
      // Ignore storage errors (private mode/quota)
    }
  };

  const getLastActivityTimestamp = (): number => {
    try {
      const raw = localStorage.getItem(ACTIVITY_SYNC_KEY);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    } catch {
      // Ignore storage errors
    }
    return Date.now();
  };

  const triggerInactivityLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Inactivity logout error:', error);
    } finally {
      setUser(null);

      const currentPath =
        typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : '/';
      const loginUrl = `/sign-in?reason=inactive&redirect=${encodeURIComponent(currentPath)}`;
      if (typeof window !== 'undefined' && window.location.pathname !== '/sign-in') {
        window.location.assign(loginUrl);
      }
    }
  };

  const resetInactivityTimer = (activityTs?: number) => {
    if (!user) return;

    const now = activityTs ?? Date.now();
    const elapsed = now - getLastActivityTimestamp();
    const remainingMs = INACTIVITY_TIMEOUT_MS - elapsed;

    clearInactivityTimer();

    if (remainingMs <= 0) {
      void triggerInactivityLogout();
      return;
    }

    inactivityTimerRef.current = setTimeout(() => {
      void triggerInactivityLogout();
    }, remainingMs);
  };

  const refreshUser = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success && data.user) {
        setUser(data.user);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'An error occurred during login' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
      clearInactivityTimer();
      try {
        localStorage.removeItem(ACTIVITY_SYNC_KEY);
      } catch {
        // Ignore storage errors
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    if (!user) {
      clearInactivityTimer();
      return;
    }

    const markActivity = () => {
      const now = Date.now();
      persistActivityTimestamp(now);
      resetInactivityTimer(now);
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== ACTIVITY_SYNC_KEY || !event.newValue) {
        return;
      }

      const ts = Number(event.newValue);
      if (Number.isFinite(ts) && ts > 0) {
        resetInactivityTimer(ts);
      }
    };

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    window.addEventListener('storage', onStorage);

    markActivity();

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      window.removeEventListener('storage', onStorage);
      clearInactivityTimer();
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
